import { useState } from "react";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { weekRangeLabel } from "@/lib/weeks";

// Contractor: pick which assignments had no work last week.
export default function NoWorkModal({ assignments, onClose, onSaved }) {
  const week = assignments[0]?._nudge_week;
  const [checked, setChecked] = useState(() => new Set(assignments.filter((a) => a.no_work_this_week).map((a) => a.id)));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setBusy(true);
    try {
      const changes = assignments.filter((a) => !a._nudge_week_has_ts && checked.has(a.id) !== !!a.no_work_this_week);
      await Promise.all(changes.map((a) => api.post(`/assignments/${a.id}/no-work`, { on: checked.has(a.id) })));
      const n = [...checked].length;
      toast.success(n ? `Noted — no work for the week of ${weekRangeLabel(week)}. You won't get reminders for that week.` : "Cleared — you're back to a normal week.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-end sm:items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-5 border border-slate-200" data-testid="no-work-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-black">I didn't work last week</h2>
          <button type="button" onClick={onClose} data-testid="no-work-close"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-600">Week of <b>{weekRangeLabel(week)}</b>. Check the assignments you had <b>no hours</b> on — we'll stop the timesheet reminders for that week.</p>
        <div className="mt-4 space-y-2">
          {assignments.map((a) => (
            <label key={a.id} data-testid={`no-work-option-${a.id}`}
              className={`flex items-start gap-3 border rounded-md p-3 ${a._nudge_week_has_ts ? "opacity-60 bg-slate-50" : "cursor-pointer hover:bg-blue-50"} ${checked.has(a.id) && !a._nudge_week_has_ts ? "border-blue-400 bg-blue-50" : "border-slate-200"}`}>
              <input type="checkbox" className="mt-1 h-4 w-4 accent-blue-600" disabled={a._nudge_week_has_ts}
                checked={a._nudge_week_has_ts ? false : checked.has(a.id)} onChange={() => toggle(a.id)}
                data-testid={`no-work-check-${a.id}`} />
              <div>
                <div className="font-semibold">{a._project_name || "Assignment"}</div>
                {a._client_name && <div className="text-xs text-slate-500">{a._client_name}</div>}
                {a._nudge_week_has_ts && <div className="text-xs text-emerald-700 font-semibold mt-1">Timesheet already submitted for this week</div>}
              </div>
            </label>
          ))}
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 h-11 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button type="button" onClick={save} disabled={busy} data-testid="no-work-save"
            className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md disabled:opacity-60">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
